<template>
    <div class="comments-view">
        <v-data-table
            locale="en-UK"
            class="elevation-1"
            loading-text="Loading... Please wait"
            :headers="headers"
            :items="comments"
            :footer-props="footerProps"
            :loading="loading"
            :items-per-page.sync="itemsPerPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            multi-sort
            @update:items-per-page="getNextPage(1)"
            @update:page="getNextPage($event)">
            <template v-slot:top>

                <v-toolbar flat>
                    <v-toolbar-title>Comments {{ commentsUtils ? `(${commentsUtils.length})` : '' }}</v-toolbar-title>
                    <v-divider class="mx-4" inset vertical />
                    <v-spacer />

                    <v-dialog
                        v-model="editComment"
                        fullscreen
                        hide-overlay
                        transition="dialog-bottom-transition">
                        <v-card v-if="company">
                            <v-toolbar>
                                <v-btn icon @click="close">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                                <v-toolbar-title>
                                    <span class="headline">{{ formTitle(editedItem) }}</span>
                                </v-toolbar-title>
                                <v-spacer />
                                <v-toolbar-items v-if="editedItem.id && mdAndUp">
                                    <v-btn :color="editedItem.approved ? '#FF5252' : '#1ac38d'" text :disabled="loading" @click="approveComment">
                                        {{ editedItem.approved ? 'Disapprove' : 'Approve' }}
                                    </v-btn>
                                </v-toolbar-items>
                            </v-toolbar>
                            <v-container>
                                <div class="d-flex justify-space-between align-start pt-4">
                                    <h2 class="d-flex align-center justify-start pb-2">
                                        <v-icon
                                            class="pr-2"
                                            :color="scoreColor(company.score).color">
                                            mdi-checkbox-blank-circle
                                        </v-icon>
                                        {{ company.naziv }}
                                    </h2>
                                    <v-btn small depressed color="primary" @click="getCompanyUrl(company)">
                                        Open
                                        <v-icon small right>mdi-open-in-new</v-icon>
                                    </v-btn>
                                </div>
                                <div class="d-flex align-center justify-start pb-4 mb-6">
                                    <div class="text-body-2 pr-4">
                                        <v-icon>mdi-map-marker</v-icon>
                                        {{ company.grad }}
                                    </div>
                                    <v-chip label small class="text-body-2 mr-4">{{ company.javna ? 'PUBLIC' : 'PRIVATE' }}</v-chip>
                                    <v-rating
                                        :value="company.social.rating"
                                        size="24"
                                        :background-color="company.social.rating ? 'primary' : 'gray lighten-4'"
                                        dense readonly half-increments>
                                    </v-rating>
                                </div>
                                <v-card elevation="0" outlined>
                                    <v-card-title class="d-flex justify-space-between py-2">
                                        <span>{{ editedItem.name }}</span>
                                        <span class="text-caption text--disabled">
                                            {{ formatDate(editedItem.date_added, 'Invalid Date') }}
                                        </span>
                                    </v-card-title>
                                    <v-card-text class="pb-2">
                                        <p class="text-body-1">{{ editedItem.email }}</p>
                                        <v-rating
                                            v-if="editedItem.rating"
                                            :value="editedItem.rating"
                                            size="18"
                                            class="pb-2"
                                            :background-color="editedItem.rating ? 'primary' : 'gray lighten-4'"
                                            dense readonly>
                                        </v-rating>
                                        <p class="text-body-1 mb-0" v-html="editedItem.message"></p>
                                    </v-card-text>
                                </v-card>

                                <v-timeline dense v-if="company.social.comments.length">
                                    <v-timeline-item
                                        v-for="comment of company.social.comments"
                                        :key="comment.id"
                                        :color="comment.id === editedItem.id ? '#1ac38d' : 'primary'"
                                        :icon="comment.id === editedItem.id ? 'mdi-star' : ''"
                                        left>
                                        <v-card elevation="0" outlined>
                                            <v-card-title class="d-flex justify-space-between py-2 pr-2">
                                                <span>{{ comment.name }}</span>
                                                <div class="text-caption text--disabled">
                                                    <span class="pr-3">{{ formatDate(comment.date_added, 'Invalid Date') }}</span>
                                                    <v-btn icon color="primary" @click="getSingleComment(comment.id)">
                                                        <v-icon small>mdi-link</v-icon>
                                                    </v-btn>
                                                </div>
                                            </v-card-title>
                                            <v-card-text class="pb-2">
                                                <v-rating
                                                    v-if="comment.rating"
                                                    :value="comment.rating"
                                                    size="18"
                                                    class="pb-2"
                                                    :background-color="comment.rating ? 'primary' : 'gray lighten-4'"
                                                    dense readonly>
                                                </v-rating>
                                                <p class="text-body-1 mb-0" v-html="comment.message"></p>
                                            </v-card-text>
                                        </v-card>
                                    </v-timeline-item>
                                </v-timeline>

                                <div class="text--disabled py-4" v-else>
                                    There are no approved comments for this company yet.
                                </div>
                            </v-container>
                        </v-card>
                    </v-dialog>

                    <v-btn
                        class="ml-2"
                        :disabled="itemsPerPage < 0"
                        @click="initialize({refreshPage: true})"
                        text icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-toolbar>
            </template>

            <template v-slot:[`item.date_added`]="{ value }">
                {{ formatDate(value, 'Date Invalid') }}
            </template>

            <template v-slot:[`item.date_approved`]="{ value }">
                {{ formatDate(value, 'Not Approved') }}
            </template>

            <template v-slot:[`item.approved`]="{ value }">
                <v-icon :color="value ? 'success': 'error'">{{ value ? 'mdi-check' : 'mdi-close '}}</v-icon>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon text class="mr-2" :disabled="loading" @click="editItem(item)">
                    <v-icon medium>mdi-comment-text</v-icon>
                </v-btn>
                <v-btn icon text :disabled="loading" @click="deleteItemDialog(item)">
                    <v-icon medium color="error">mdi-delete</v-icon>
                </v-btn>
            </template>

            <template v-slot:no-data>
                <v-btn color="primary" class="my-2" @click="initialize({refreshPage: true})">Refresh</v-btn>
            </template>

            <template v-slot:footer.prepend>
                <div class="d-flex align-center ma-2" v-if="mdAndUp">
                    <v-icon small>mdi-information-outline</v-icon>
                    <span class="ml-2 text--secondary">Use pagination arrows to load more comments from database.</span>
                </div>
            </template>

        </v-data-table>

        <SimpleDialog
            :visible="deleteDialog"
            title="Delete Comment"
            :message="`You're about to delete ${itemToDelete.name}'s comment.`"
            yes="Delete"
            no="Cancel"
            @on-yes="deleteItem(itemToDelete)"
            @on-no="deleteDialog = false">
        </SimpleDialog>

        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            <span>{{ snackText }}</span>
            <template v-slot:action="{ attrs }">
                <v-icon v-bind="attrs" text @click="snack = false">mdi-close</v-icon>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SimpleDialog from "../../components/SimpleDialog";
import {showSnack} from "@/components/mixins/showSnack";
import {scoreColor} from "@/components/mixins/scoreColor";
import firebase from "firebase/app";
import 'firebase/firestore';

export default {
    name: 'Comments',

    components: {
        SimpleDialog
    },

    mixins: [showSnack, scoreColor],

    data() {
        return {
            editComment: false,
            deleteDialog: false,
            itemToDelete: {},
            loading: true,
            page: 1,
            pageMax: 1,
            itemsPerPage: 5,
            sortBy: ['approved', 'date_added'],
            sortDesc: [false, false],
            headers: [
                {
                    text: 'Name',
                    align: 'start',
                    sortable: true,
                    value: 'name',
                    cellClass: 'body-2 font-weight-medium'
                },
                {
                    text: 'Date Added',
                    value: 'date_added',
                    sortable: true,
                    align: 'center'
                },
                {
                    text: 'Date Approved',
                    value: 'date_approved',
                    sortable: true,
                    align: 'center'
                },
                {
                    text: 'Approved',
                    value: 'approved',
                    sortable: true,
                    align: 'center'
                },
                {
                    text: 'Actions',
                    value: 'actions',
                    sortable: false,
                    align: 'right'
                },
            ],
            footerProps: {
                itemsPerPageOptions: [5, 10, -1],
                itemsPerPageText: 'Comments per page',
                showCurrentPage: true
            },

            editedIndex: -1,
            editedItem: {},
            commentSchema: {
                company_id: null,
                name: null,
                email: null,
                rating: null,
                message: null,
                date_added: null,
                date_approved: null,
                approved_by: null,
                approved: false
            }
        };
    },

    computed: {
        ...mapGetters({
            commentsUtils: 'dashboard/commentsUtils',
            comments: 'comments/getComments',
            company: 'comments/getCompany',
            user: 'auth/user'
        }),
        mdAndUp() {
            return this.$vuetify.breakpoint.mdAndUp;
        }
    },

    watch: {
        editComment(val) {
            val || this.close();
        },
        sortBy() {
            this.initialize({refreshPage: true});
        },
        sortDesc() {
            this.initialize({refreshPage: true});
        }
    },

    created() {
        this.editedItem = Object.assign({}, this.commentSchema);
        this.initialize({refreshPage: true});
    },

    beforeRouteLeave(to, from, next) {
        if (this.editComment) {
            this.close();
            next(false);
        } else {
            next();
        }
    },

    methods: {
        ...mapActions({
            loadCompany: 'comments/loadCompany',
            loadComments: 'comments/loadComments',
            updateComment: 'comments/updateComment',
            removeComment: 'comments/removeComment'
        }),
        formatDate(value, invalidText) {
            return value && !isNaN(value) ? new Date(Number(value.seconds) * 1000).toLocaleString('en-UK') : invalidText;
        },
        formTitle(comment) {
            return comment.approved ? 'Approved Comment' : 'Review Comment';
        },
        async initialize(args) {
            this.loading = true;
            try {
                const { refreshPage } = args;
                const { itemsPerPage, sortBy, sortDesc } = this;
                const orderBy = [sortBy[0] || 'date_added', sortDesc[0] ? 'desc' : 'asc'];
                const params = {
                    orderBy,
                    itemsPerPage,
                    refreshPage
                }
                await this.loadComments(params);
            } catch (e) {
                console.error(e);
            }
            this.loading = false;
        },
        editItem(item) {
            this.editedIndex = this.comments.findIndex(comment => comment.id === item.id);
            this.editedItem = Object.assign({}, item);
            this.editComment = true;
            this.loadCompany(item.company_id);
        },
        async getSingleComment(id) {
            try {
                this.close();
                const doc = await firebase.firestore().collection(process.env.VUE_APP_COLLECTION_COMMENTS).doc(id).get();
                const comment = doc.data()
                this.editItem(comment);
            } catch (e) {
                this.showSnack('The comment could not be loaded', 'error');
                console.error(e);
            }
        },
        close() {
            this.editComment = false;
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.commentSchema);
                this.editedIndex = -1;
            });
        },
        deleteItemDialog(item) {
            this.deleteDialog = true;
            this.itemToDelete = Object.assign({}, item);
        },
        async approveComment() {
            if (this.editedIndex < 0) {
                return;
            }
            this.loading = true;
            try {
                this.editedItem.approved = !this.editedItem.approved;
                this.editedItem.approved_by = this.editedItem.approved ? this.user.data.uid : null;
                await this.updateComment({index: this.editedIndex, comment: this.editedItem});
                this.showSnack(`${this.editedItem.name}'s comment has been ${this.editedItem.approved ? 'approved' : 'disapproved'}`, 'success');
            } catch (e) {
                this.showSnack(`${this.editedItem.name}'s comment ${this.editedItem.approved ? 'approval' : 'disapproval'} has failed`, 'error');
                console.error(e);
            } finally {
                this.close();
                this.loading = false;
            }
        },
        async deleteItem(item) {
            this.loading = true;
            try {
                await this.loadCompany(item.company_id);
                await this.removeComment(item);
                this.showSnack(`${item.name}'s comment has been deleted from database`, 'success');
            } catch (e) {
                this.showSnack(`${item.name}'s comment could not be deleted`, 'error');
                console.error(e);
            } finally {
                this.deleteDialog = false;
                this.itemToDelete = {};
                this.loading = false;
            }
        },
        getNextPage(event) {
            this.pageMax = Math.ceil(this.comments.length / this.itemsPerPage);
            if (event === this.pageMax) {
                this.initialize({refreshPage: false});
            }
            this.page = event;
        },
        resetPagination() {
            this.page = 1;
            this.pageMax = 1;
        },
        getCompanyUrl(company) {
            const slug = company.naziv.toLowerCase().split(/[ -]+/)
                .filter(word => word.length || !isNaN(parseInt(word, 10)))
                .map(word => word.replace(/[^a-z0-9čšćđž]/g, '')).join('-');
            window.open(`${window.location.origin}/pravno-lice/${slug}/${company.id}`)
        }
    }
};
</script>

<style lang="scss" scoped>
.comments-view {

}
</style>
